/**
 * File: siteTutorialsData.ts
 * Path: apps/admin/src/shared/utils/siteTutorialsData.ts
 * Description: The tutorials for the rest of the admin: the website content
 * (Portfolio, Academy, Services, Reviews, Search & AI), the tools, referrals,
 * quotes, notifications, diagnostics, settings, the section menus and the
 * website analytics. Button names are the live ones.
 */

import { ADMIN_PATHS } from "@idk/config";
import { HEAD, PAGE_ACTIONS, type Tutorial } from "./tutorialTypes";

const P = ADMIN_PATHS;

export const SITE_TUTORIALS: Tutorial[] = [
  {
    id: "analytics-read",
    title: "Read your website traffic",
    hint: "2 steps",
    pages: [P.dashboard],
    group: "Website: Analytics",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "Dates",
        text: "Pick the dates to look at. The numbers and the chart below follow them.",
      },
      {
        target: HEAD,
        title: "Website Analytics",
        text: "This page shows how many people visit the website and what they do.",
      },
    ],
  },
  {
    id: "portfolio-add",
    title: "Add a case study to the portfolio",
    hint: "2 steps",
    pages: [P.portfolio.root],
    group: "Website: Portfolio",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "New case study",
        text: "Press New case study to start one. Fill in the client, what you did and the pictures, then press Save Case Study.",
      },
      {
        target: "main table",
        title: "Your case studies",
        text: "Each row is one case study. Open its menu to edit, preview or delete it. A change reaches the public site at the next deploy.",
      },
    ],
  },
  {
    id: "portfolio-edit",
    title: "Edit a case study",
    hint: "2 steps",
    pages: [P.portfolio.edit(":id"), P.portfolio.new],
    group: "Website: Portfolio",
    steps: [
      {
        target: HEAD,
        title: "The editor",
        text: "The tabs hold the general details, the hero picture, the numbers, the credits and the assets. Work through them one at a time.",
      },
      {
        target: PAGE_ACTIONS,
        title: "Save",
        text: "Press Save Case Study when you are done. The change reaches the public website at the next deploy.",
      },
    ],
  },
  {
    id: "academy-add",
    title: "Write an Academy article",
    hint: "2 steps",
    pages: [P.academy.root],
    group: "Website: Academy",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "New article",
        text: "Press New article to start a new post. Add the title, the picture and the words, then save it.",
      },
      {
        target: "main table",
        title: "Your articles",
        text: "Each row is one article. Open its menu to edit, preview or delete it.",
      },
    ],
  },
  {
    id: "academy-edit",
    title: "Edit an article",
    hint: "1 step",
    pages: [P.academy.edit(":id"), P.academy.new],
    group: "Website: Academy",
    steps: [
      {
        target: HEAD,
        title: "The editor",
        text: "Fill in the identity, the hero picture and the words in each section. Preview it before you save.",
      },
    ],
  },
  {
    id: "services-edit",
    title: "Change the wording of a service",
    hint: "2 steps",
    pages: [P.services.root],
    group: "Website: Services",
    steps: [
      {
        target: HEAD,
        title: "The list",
        text: "These are the services on the website, in the order it lists them. Prices are set in code, so only the wording is edited here.",
      },
      {
        target: "main",
        title: "Open one",
        text: "Press a service to edit its wording, features and questions. The change reaches the public site at the next deploy.",
      },
    ],
  },
  {
    id: "service-detail",
    title: "Edit one service",
    hint: "1 step",
    pages: [P.services.detail(":id")],
    group: "Website: Services",
    steps: [
      {
        target: HEAD,
        title: "The service",
        text: "Change its wording, features and questions. The price is shown but not edited here.",
      },
    ],
  },
  {
    id: "reviews-link",
    title: "Ask a client for a Google review",
    hint: "2 steps",
    pages: [P.reviews],
    group: "Website: Reviews",
    steps: [
      {
        target: "main section",
        title: "Your review link",
        text: "Copy the Google review link here and send it to a happy client.",
      },
      {
        target: HEAD,
        title: "Show real reviews",
        text: "The cards below send review requests, and show real reviews on the case study they are about.",
      },
    ],
  },
  {
    id: "seo-weakest",
    title: "Find the page that needs work for search",
    hint: "2 steps",
    pages: [P.seo],
    group: "Website: Search & AI",
    steps: [
      {
        target: HEAD,
        title: "Weakest first",
        text: "Every case study and article is scored on how well it will show up in Google and AI answers. The weakest is first.",
      },
      {
        target: "main",
        title: "Open one",
        text: "Press a row to see its checks and what to fix. Fix them in the case study or article itself.",
      },
    ],
  },
  {
    id: "tools-audit",
    title: "Audit any website",
    hint: "2 steps",
    pages: [P.tools.audit],
    group: "Tools",
    steps: [
      {
        target: "main form, main section",
        title: "The website",
        text: "Type the website address and choose how to send the result. To audit a lead, use the Website audit tab on their page instead.",
      },
      {
        target: HEAD,
        title: "Run it",
        text: "Press Run audit and download to get the PDF report. The email option sends it for you.",
      },
    ],
  },
  {
    id: "tools-brand",
    title: "Build a brand profile",
    hint: "1 step",
    pages: [P.tools.brand],
    group: "Tools",
    steps: [
      {
        target: HEAD,
        title: "Brand Kit",
        text: "Answer the questions about a business, or pick a ready-made look for a fast profile. The result can be downloaded.",
      },
    ],
  },
  {
    id: "referrals-manage",
    title: "Manage referral partners",
    hint: "2 steps",
    pages: [P.referrals.root],
    group: "CRM: Referrals",
    steps: [
      {
        target: HEAD,
        title: "Add a partner",
        text: "Type the partner's name, email and commission %, then press Create affiliate. They get a code to share.",
      },
      {
        target: "main section",
        title: "Your partners",
        text: "Press Referrals on a row to see what they sent you and mark commission paid. Edit sets special rates per service.",
      },
    ],
  },
  {
    id: "quote-new",
    title: "Make a quote",
    hint: "2 steps",
    pages: [P.quotes.new],
    group: "CRM: Quotes",
    steps: [
      {
        target: '[aria-label="Quote lines"]',
        title: "Quote lines",
        text: "Pick what they are getting from your price list. Set how many and the price for each line.",
      },
      {
        target: '[aria-label="Quote total"]',
        title: "The total",
        text: "The total is worked out for you. Check the preview, then make the PDF.",
      },
    ],
  },
  {
    id: "quote-deal-terms",
    title: "Change the terms for one deal",
    hint: "3 steps",
    pages: [P.quotes.new],
    group: "CRM: Quotes",
    steps: [
      {
        target: '[aria-label="Quote lines"]',
        title: "Open the terms",
        text: "Add the service first. Under its price, press Terms for this deal. It starts as your standard terms, with Standard: shown under each field.",
      },
      {
        target: '[aria-label="Quote lines"]',
        title: "Change what you need",
        text: "Change the rounds of changes, the ways to pay, the time or the words. Changed fields turn gold and the preview follows. The number beside the title counts them.",
      },
      {
        target: '[aria-label="Quote lines"]',
        title: "Go back to standard",
        text: "Press Reset to standard to drop every change for that line. The clauses about source code, POPIA and stopping cannot be changed.",
      },
    ],
  },
  {
    id: "quote-edit",
    title: "Edit a quote you already sent",
    hint: "4 steps",
    pages: [P.quotes.edit(":id")],
    group: "CRM: Quotes",
    steps: [
      {
        target: '[data-tour="quote-edit-banner"]',
        title: "Which version",
        text: "Press the pencil on a sent quote, in Money's Agreements list or on a lead's or client's quote card. This note says which quote it is and which version you are making.",
      },
      {
        target: '[aria-label="Quote lines"]',
        title: "Change what you need",
        text: "Change the lines, the prices or the note. A quote that was accepted or has a contract says Locked and cannot be changed.",
      },
      {
        target: '[data-tour="quote-edit-hold"]',
        title: "Price hold and earlier versions",
        text: "The price hold keeps its date unless you change it. Earlier versions stay on file, and you can open what they were sent.",
      },
      {
        target: '[data-tour="quote-edit-save"]',
        title: "Save the new version",
        text: "Press Save version 2 and send it to email the new PDF. Save version 2 keeps it without sending. The number goes up by one each time.",
      },
    ],
  },
  {
    id: "quote-estimate",
    title: "Send an estimate to someone new",
    hint: "1 step",
    pages: [P.quotes.estimate],
    group: "CRM: Quotes",
    steps: [
      {
        target: HEAD,
        title: "Estimate",
        text: "For someone who asked for a price before you met. Answer the questions for them and they get a PDF called an estimate.",
      },
    ],
  },
  {
    id: "notifications-read",
    title: "Read and clear your notifications",
    hint: "2 steps",
    pages: [P.notifications],
    group: "System",
    steps: [
      {
        target: '[aria-label="Notifications"]',
        title: "The list",
        text: "Every notification sent to you. Press one to open the page it is about.",
      },
      {
        target: PAGE_ACTIONS,
        title: "Mark all read",
        text: "Clears the unread count in one press. Nothing is deleted.",
      },
    ],
  },
  {
    id: "diagnostics-check",
    title: "Check that notifications are working",
    hint: "2 steps",
    pages: [P.diagnostics],
    group: "System",
    steps: [
      {
        target: PAGE_ACTIONS,
        title: "Run again and copy",
        text: "Run the checks again after you fix something. Copy the result to send it to whoever helps you.",
      },
      {
        target: HEAD,
        title: "What each check says",
        text: "Each check says if something works, and what to do if it does not.",
      },
    ],
  },
  {
    id: "settings-profile",
    title: "Change your profile and how the admin looks",
    hint: "1 step",
    pages: [P.settings],
    group: "System",
    steps: [
      {
        target: "#settings-tab-profile",
        title: "Profile",
        text: "Your login, how the admin looks on this device, and which version you are on. The other tabs show only for roles that may use them.",
      },
    ],
  },
  {
    id: "menu-find",
    title: "Find a page",
    hint: "1 step",
    pages: [P.menu.crm, P.menu.accounting, P.menu.cms],
    group: "System",
    steps: [
      {
        target: "main nav",
        title: "This section",
        text: "Every page in this section is listed here with one line on what it does. Press one to open it.",
      },
    ],
  },
  {
    id: "howto-find",
    title: "Find a tutorial for any job",
    hint: "1 step",
    pages: [P.tutorials],
    group: "System",
    steps: [
      {
        target: HEAD,
        title: "All tutorials",
        text: "Every tutorial in the admin is listed here by section. Press Show me and the admin opens the page and lights up each thing to press. You get here from All tutorials in any Help box.",
      },
    ],
  },
  {
    id: "numbers-glossary",
    title: "Look up what a number means",
    hint: "1 step",
    pages: [P.numbers.glossary],
    group: "System",
    steps: [
      {
        target: HEAD,
        title: "Glossary",
        text: "Each number on Today is explained here in plain words.",
      },
    ],
  },
];
